import { GridColDef } from '@mui/x-data-grid';

/**
 * RFC 4180 compliant CSV export with Excel/Numbers compatibility.
 * - UTF-8 BOM for proper encoding in Excel and macOS Numbers
 * - Proper quote escaping (double quotes escaped as "")
 * - Handles null/undefined values
 */
export function exportToCsv<T extends object>(
    columns: GridColDef[],
    rows: T[],
    filename: string,
    options?: {
        /** Only export these columns (by field name). If not provided, exports all non-hidden columns. */
        includeColumns?: string[];
        /** Custom formatters for specific fields. Receives the cell value and full row. */
        formatters?: Record<string, (value: unknown, row: T) => string>;
    },
): void {
    const { includeColumns, formatters = {} } = options ?? {};

    // Filter columns to export
    const exportColumns = columns.filter((col) => {
        if (includeColumns) {
            return includeColumns.includes(col.field);
        }
        return true;
    });

    // Build header row
    const header = exportColumns.map((col) => escapeCell(col.headerName ?? col.field)).join(',');

    // Build data rows
    const dataRows = rows.map((row) =>
        exportColumns
            .map((col) => {
                const rawValue = getNestedValue(row, col.field);

                // Use custom formatter if provided
                if (formatters[col.field]) {
                    return escapeCell(formatters[col.field](rawValue, row));
                }

                return escapeCell(formatValue(rawValue));
            })
            .join(','),
    );

    // Combine with UTF-8 BOM for Excel/Numbers compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + [header, ...dataRows].join('\r\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();

    URL.revokeObjectURL(url);
}

/**
 * Escape a cell value for CSV (RFC 4180 compliant).
 * - Wraps in quotes if contains comma, quote, or newline
 * - Escapes quotes by doubling them
 */
function escapeCell(value: string): string {
    if (
        value.includes('"') ||
        value.includes(',') ||
        value.includes('\n') ||
        value.includes('\r')
    ) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

/**
 * Format a value for CSV export.
 */
function formatValue(value: unknown): string {
    if (value == null) {
        return '';
    }

    if (value instanceof Date) {
        return value.toLocaleString();
    }

    if (typeof value === 'object') {
        // Handle arrays
        if (Array.isArray(value)) {
            return value.map((v) => formatValue(v)).join(', ');
        }
        // Handle objects - stringify
        return JSON.stringify(value);
    }

    return String(value);
}

/**
 * Get a nested value from an object using dot notation (e.g., "info.deviceName").
 */
function getNestedValue(obj: object, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => {
        if (current && typeof current === 'object' && key in current) {
            return (current as Record<string, unknown>)[key];
        }
        return undefined;
    }, obj);
}
